import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { useTranslation } from "react-i18next";
import PromotionPackageExecution from "../../../components/PromotionPackageExecution";
import PromotionPackageRetirement from "../../../components/PromotionPackageRetirement";
import { api } from "../../../utils/helpers";
import { activeWerke, packageStates, usesWerke } from "../context/TransportV2Context";
import "../../TransportApprovals.css";

const endpoint = "/api/cicd/transport/promotions";
// Use only the server's physical target IDs from the current selected-plant comparison.
export function retirementTargets(state) {
  const result = new Map();
  for (const draft of state.selectedPackages?.length > 1 ? packageStates(state) : [state]) {
    const targetIds = draft.space?.qasInstance?.id ? [draft.space.qasInstance.id] : draft.targetInstanceIds || [];
    const plants = usesWerke(draft) ? activeWerke(draft).map(plant => plant.kennung) : ["std"];
    for (const target of targetIds) for (const plant of plants) {
      const entry = draft.precheckByBucket?.[`${target}__${plant}`];
      if (!entry || entry.error || typeof entry.targetPackageId !== "string" || !entry.targetPackageId
        || (entry.instance?.id && entry.instance.id !== target)
        || (entry.werk?.kennung && entry.werk.kennung !== plant)) continue;
      result.set(entry.targetPackageId, { packageId: entry.targetPackageId, packageName: entry.targetPackageName || entry.targetPackageId });
    }
  }
  return [...result.values()];
}

export default function ReviewPackageRetirement({ targets, spaceId, canonicalSpaceId, user, blocked, onLockChange, onRemoved }) {
  const { t } = useTranslation("freigabe");
  const [reading, setReading] = useState(null);
  const [data, setData] = useState(null);
  const [item, setItem] = useState(null);
  const [executionTarget, setExecutionTarget] = useState(null);
  const [error, setError] = useState(null);
  const [mutating, setMutating] = useState(false);
  const alive = useRef(true);
  const actionLock = useRef(false);
  const version = useRef(0);
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const latest = useRef({ isAdmin, blocked });
  latest.current = { isAdmin, blocked };
  useEffect(() => { alive.current = true; return () => { alive.current = false; version.current += 1; onLockChange(false); }; }, [onLockChange]);
  useEffect(() => {
    if (!isAdmin) { version.current += 1; actionLock.current = false; setItem(null); setExecutionTarget(null); setData(null); setReading(null); onLockChange(false); }
  }, [isAdmin, onLockChange]);
  const read = async () => {
    const result = await api(`${endpoint}?spaceId=${encodeURIComponent(spaceId)}`, { cache: "no-store", retry: false });
    if (result?.spaceId !== canonicalSpaceId || !result.configured || result.configuredPackagesError || result.topicError
      || !Array.isArray(result.configuredPackages) || result.configuredPackages.some(row => !row || typeof row.packageId !== "string" || typeof row.revision !== "string")) {
      throw new Error("PACKAGE_RETIREMENT_UNAVAILABLE");
    }
    return result;
  };
  const inspect = async target => {
    if (!isAdmin || blocked || actionLock.current) return;
    actionLock.current = true; onLockChange(true); setReading(target.packageId); setError(null);
    const currentVersion = ++version.current;
    try {
      const result = await read();
      if (!alive.current || currentVersion !== version.current || !latest.current.isAdmin || latest.current.blocked) return;
      const rows = result.configuredPackages.filter(row => row.packageId === target.packageId);
      const current = rows.length === 1 ? rows[0] : null;
      const canResume = current?.pendingRetirement && result.canManageRetirements === true;
      if (!current || (result.canManagePackages !== true && !canResume)) throw new Error("PACKAGE_RETIREMENT_UNAVAILABLE");
      setData(result); setItem(current);
    } catch {
      if (alive.current && currentVersion === version.current) { setError(t("packageRetirement.unavailable")); onLockChange(false); }
    } finally {
      if (alive.current && currentVersion === version.current) { setReading(null); actionLock.current = false; if (latest.current.blocked) onLockChange(false); }
    }
  };
  const current = data?.configuredPackages.find(row => row.packageId === item?.packageId);
  const refreshAfterMutation = async () => {
    if (!alive.current) return;
    try { const result = await read(); if (alive.current) { setData(result); setError(null); } }
    catch { if (alive.current) { setData(null); setError(t("packageRetirement.unavailable")); } }
    finally { if (alive.current) setMutating(false); }
  };
  if (!isAdmin || !targets.length) return null;
  return <Box className="review-package-retirement" sx={{ my: 2 }}>
    <Typography component="p" sx={{ mb: 1, color: "text.secondary" }}>{t("packageRetirement.reviewHint")}</Typography>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
      {targets.map(target => <Box key={target.packageId} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button variant="text" disabled={!!reading || !!item || !!executionTarget || mutating}
          onClick={() => { setExecutionTarget(target); onLockChange(true); }} aria-haspopup="dialog"
          aria-label={t("packageExecution.inspectNamed", { id: target.packageId })}>{t("packageExecution.title")}</Button>
        <Button variant="text" color="error" startIcon={reading === target.packageId
        ? <CircularProgress size={18}/> : <DeleteOutlineRounded/>} sx={{ whiteSpace: "normal", overflowWrap: "anywhere", textAlign: "left" }}
        disabled={blocked || !!reading || !!item || !!executionTarget || mutating} onClick={() => inspect(target)}
        aria-haspopup="dialog">{t("packageRetirement.removeNamed", { id: target.packageId })}</Button></Box>)}
    </Box>
    {error && !item && <Alert severity="error">{error}</Alert>}
    {executionTarget && <PromotionPackageExecution item={executionTarget} spaceId={spaceId} canonicalSpaceId={canonicalSpaceId} allowed={isAdmin}
      onStart={() => { setMutating(true); onLockChange(true); }} onEnd={refreshAfterMutation}
      onClose={() => { setExecutionTarget(null); onLockChange(false); }}
      onRemoved={() => { setExecutionTarget(null); onRemoved(); }}/>}
    {item && <PromotionPackageRetirement item={item} current={current} spaceId={spaceId} canonicalSpaceId={canonicalSpaceId}
      disabled={blocked || !!error || data?.canManagePackages !== true}
      canResume={!blocked && !error && data?.canManageRetirements === true}
      onStart={() => { setMutating(true); onLockChange(true); }} onEnd={refreshAfterMutation}
      onClose={done => {
        setItem(null);
        if (done) onRemoved();
        else onLockChange(!!current?.pendingRetirement || !!error);
      }}/>}
  </Box>;
}
